import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import { textInputStyle } from '../components/ControlPanelBoxes';

// Shared team to-do list. Logins are shared (admin / superuser), so ownership
// comes from staff.initials — same approach as blocked_order_assignments.

const selectStyle = {
  background: 'var(--bg3)', color: 'var(--white)',
  border: '1px solid var(--border2)', borderRadius: 4,
  padding: '4px 10px', fontFamily: 'var(--font-body)',
  fontSize: 12, cursor: 'pointer',
};

// Local (NZ) date as YYYY-MM-DD — toISOString() would shift us back a day.
const todayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const daysUntil = (dueDate) => {
  const [y, m, d] = dueDate.split('-').map(Number);
  const [ty, tm, td] = todayISO().split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86400000);
};

const DueCell = ({ dueDate, done }) => {
  if (!dueDate) return <span style={{ color: 'var(--muted2)', fontSize: 12 }}>—</span>;
  if (done) return <span style={{ color: 'var(--muted)', fontSize: 12 }}>{dueDate}</span>;

  const diff = daysUntil(dueDate);
  let label = `in ${diff} days`;
  let color = 'var(--muted)';
  if (diff < 0) {
    label = diff === -1 ? '1 day overdue' : `${Math.abs(diff)} days overdue`;
    color = 'var(--red)';
  } else if (diff === 0) {
    label = 'due today';
    color = 'var(--gold)';
  } else if (diff === 1) {
    label = 'tomorrow';
  }

  return (
    <span style={{ color, fontSize: 12, fontWeight: diff <= 0 ? 600 : 400 }}>
      {dueDate} · {label}
    </span>
  );
};

const TodoList = () => {
  const navigate = useNavigate();

  const [todos, setTodos]         = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState({}); // { [id]: true } while a save is in flight
  const [staffFilter, setStaffFilter]     = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [newTodo, setNewTodo] = useState({ title: '', assigned_initials: '', due_date: '' });
  const [adding, setAdding]   = useState(false);

  const loadStaff = useCallback(async () => {
    const { data } = await supabase.from('staff').select('name, initials').order('name');
    setStaffList(data || []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('admin_todos')
      .select('id, title, assigned_initials, due_date, done, completed_at, created_at');
    if (error) {
      console.error('Failed to load to-dos:', error);
      setTodos([]);
      setLoading(false);
      return;
    }
    setTodos(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); loadStaff(); }, [load, loadStaff]);

  const updateField = async (id, field, value) => {
    setSaving(prev => ({ ...prev, [id]: true }));
    const { error } = await supabase
      .from('admin_todos')
      .update({ [field]: value || null })
      .eq('id', id);
    setSaving(prev => ({ ...prev, [id]: false }));
    if (error) {
      alert(`Failed to update: ${error.message}`);
      return;
    }
    setTodos(prev => prev.map(t => t.id === id ? { ...t, [field]: value || null } : t));
  };

  const toggleDone = async (todo) => {
    const done = !todo.done;
    const completed_at = done ? new Date().toISOString() : null;
    setSaving(prev => ({ ...prev, [todo.id]: true }));
    const { error } = await supabase
      .from('admin_todos')
      .update({ done, completed_at })
      .eq('id', todo.id);
    setSaving(prev => ({ ...prev, [todo.id]: false }));
    if (error) {
      alert(`Failed to update: ${error.message}`);
      return;
    }
    setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, done, completed_at } : t));
  };

  const handleAdd = async () => {
    if (!newTodo.title.trim()) return;
    setAdding(true);
    const { data, error } = await supabase
      .from('admin_todos')
      .insert({
        title:             newTodo.title.trim(),
        assigned_initials: newTodo.assigned_initials || null,
        due_date:          newTodo.due_date || null,
      })
      .select()
      .single();
    setAdding(false);
    if (error) {
      alert(`Failed to add: ${error.message}`);
      return;
    }
    setTodos(prev => [...prev, data]);
    setNewTodo({ title: '', assigned_initials: '', due_date: '' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this to-do?')) return;
    const { error } = await supabase.from('admin_todos').delete().eq('id', id);
    if (error) {
      alert(`Failed to delete: ${error.message}`);
      return;
    }
    setTodos(prev => prev.filter(t => t.id !== id));
  };

  const matchesStaff = (t) => {
    if (!staffFilter) return true;
    if (staffFilter === '__unassigned__') return !t.assigned_initials;
    return t.assigned_initials === staffFilter;
  };

  // Open items: soonest due first, undated last, then oldest first.
  const open = todos
    .filter(t => !t.done && matchesStaff(t))
    .sort((a, b) => {
      if ((a.due_date || '') !== (b.due_date || '')) {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date < b.due_date ? -1 : 1;
      }
      return (a.created_at || '').localeCompare(b.created_at || '');
    });

  // Completed items: most recently ticked off first.
  const completed = todos
    .filter(t => t.done && matchesStaff(t))
    .sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || ''));

  const overdueCount = open.filter(t => t.due_date && daysUntil(t.due_date) < 0).length;

  const renderRow = (t) => (
    <tr key={t.id} style={t.done ? { opacity: 0.6 } : undefined}>
      <td style={{ width: 34, textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={t.done}
          disabled={!!saving[t.id]}
          onChange={() => toggleDone(t)}
          style={{ cursor: 'pointer', width: 15, height: 15 }}
        />
      </td>
      <td>
        <input
          type="text"
          defaultValue={t.title}
          disabled={!!saving[t.id]}
          onBlur={(ev) => {
            const val = ev.target.value.trim();
            if (!val) { ev.target.value = t.title; return; }
            if (val !== t.title) updateField(t.id, 'title', val);
          }}
          style={{ ...textInputStyle, textDecoration: t.done ? 'line-through' : 'none' }}
        />
      </td>
      <td style={{ width: 180 }}>
        <select
          value={t.assigned_initials || ''}
          disabled={!!saving[t.id]}
          onChange={(ev) => updateField(t.id, 'assigned_initials', ev.target.value)}
          style={{ ...selectStyle, width: '100%' }}
        >
          <option value="">Unassigned</option>
          {staffList.map(s => (
            <option key={s.initials} value={s.initials}>{s.name} ({s.initials})</option>
          ))}
        </select>
      </td>
      <td style={{ width: 150 }}>
        <input
          type="date"
          value={t.due_date || ''}
          disabled={!!saving[t.id]}
          onChange={(ev) => updateField(t.id, 'due_date', ev.target.value)}
          style={textInputStyle}
        />
      </td>
      <td style={{ width: 170 }}>
        <DueCell dueDate={t.due_date} done={t.done} />
      </td>
      <td style={{ width: 80 }}>
        <button
          className="action-btn action-btn--ghost"
          style={{ fontSize: 11 }}
          onClick={() => handleDelete(t.id)}
        >
          Delete
        </button>
      </td>
    </tr>
  );

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/utilities')}>← Utilities</button>
        <span className="page-nav__title">To-Do List</span>
      </div>

      <section className="section">
        <SectionHeader
          title="To-Do List"
          count={open.length}
          onRefresh={load}
          loading={loading}
          actions={
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className={`action-btn ${showCompleted ? 'action-btn--active' : 'action-btn--ghost'}`}
                onClick={() => setShowCompleted(v => !v)}
                style={{ fontSize: 12 }}
              >
                {showCompleted ? 'Show Completed ✓' : 'Show Completed'}
              </button>
              <select
                value={staffFilter}
                onChange={e => setStaffFilter(e.target.value)}
                style={selectStyle}
              >
                <option value="">All Staff</option>
                <option value="__unassigned__">Unassigned</option>
                {staffList.map(s => (
                  <option key={s.initials} value={s.initials}>{s.name} ({s.initials})</option>
                ))}
              </select>
            </div>
          }
        />
        <p className="section__desc">
          Shared team to-do list — everyone sees the same items. Assign an owner and a due date,
          then tick items off as they are done. Overdue items are shown in red, due today in gold.
          {overdueCount > 0 && (
            <strong style={{ color: 'var(--red)' }}> {overdueCount} overdue.</strong>
          )}
        </p>

        {loading && <div className="loading-row">Loading…</div>}

        {!loading && (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 34 }}></th>
                <th>Task</th>
                <th style={{ width: 180 }}>Assigned To</th>
                <th style={{ width: 150 }}>Due</th>
                <th style={{ width: 170 }}>Status</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {open.map(renderRow)}

              <tr>
                <td></td>
                <td>
                  <input
                    type="text"
                    placeholder="New task…"
                    value={newTodo.title}
                    onChange={(ev) => setNewTodo(prev => ({ ...prev, title: ev.target.value }))}
                    onKeyDown={(ev) => { if (ev.key === 'Enter') handleAdd(); }}
                    style={textInputStyle}
                  />
                </td>
                <td>
                  <select
                    value={newTodo.assigned_initials}
                    onChange={(ev) => setNewTodo(prev => ({ ...prev, assigned_initials: ev.target.value }))}
                    style={{ ...selectStyle, width: '100%' }}
                  >
                    <option value="">Unassigned</option>
                    {staffList.map(s => (
                      <option key={s.initials} value={s.initials}>{s.name} ({s.initials})</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="date"
                    value={newTodo.due_date}
                    onChange={(ev) => setNewTodo(prev => ({ ...prev, due_date: ev.target.value }))}
                    style={textInputStyle}
                  />
                </td>
                <td></td>
                <td>
                  <button
                    className="action-btn action-btn--reset"
                    style={{ fontSize: 11 }}
                    disabled={!newTodo.title.trim() || adding}
                    onClick={handleAdd}
                  >
                    {adding ? 'Adding…' : 'Add'}
                  </button>
                </td>
              </tr>

              {showCompleted && completed.length > 0 && (
                <tr>
                  <td colSpan={6} style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 600, paddingTop: 14 }}>
                    Completed ({completed.length})
                  </td>
                </tr>
              )}
              {showCompleted && completed.map(renderRow)}
            </tbody>
          </table>
        )}

        {!loading && !open.length && (
          <div className="empty-row">✓ Nothing outstanding</div>
        )}
      </section>
    </main>
  );
};

export default TodoList;
